import React, { useState, useEffect, useCallback } from 'react';
import { db, auth, functions } from '../../firebase/config';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import {
    updatePassword,
    reauthenticateWithCredential,
    EmailAuthProvider,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import '../AdminSettings/AdminSettings.css';
import { getRoleLabel, isMasterAdmin, normalizeUserRole, USER_ROLES } from '../../utils/adminRoles';
import { useAuth } from '../../contexts/AuthContext';

const EditIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
);
const EyeIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
        <circle cx="12" cy="12" r="3"/>
    </svg>
);
const EyeOffIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
        <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
);
const SendIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="22" y1="2" x2="11" y2="13"/>
        <polygon points="22 2 15 22 11 13 2 9 22 2"/>
    </svg>
);
const TrashIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6"/>
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
        <path d="M10 11v6M14 11v6"/>
        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
    </svg>
);
const AdminIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
        <circle cx="12" cy="7" r="4"/>
    </svg>
);
const SpinnerIcon = () => (
    <svg className="as-spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <path d="M12 2a10 10 0 0 1 10 10"/>
    </svg>
);

function validateEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function getFriendlyError(error, fallbackText) {
    const code = error?.code || '';
    const status = error?.status || '';
    const rawMessage = error?.message || fallbackText;
    const message = String(rawMessage).replace(/^Firebase:\s*/i, '').trim();

    if (code.includes('permission-denied') || status === 'permission-denied') return 'You do not have permission to manage admin accounts.';
    if (code.includes('unauthenticated') || status === 'unauthenticated') return 'You must be signed in to continue.';
    if (code.includes('failed-precondition') || status === 'failed-precondition') return message || fallbackText;
    if (code.includes('invalid-argument') || status === 'invalid-argument') return message || fallbackText;
    if (code.includes('unavailable')) return 'Firebase is unavailable right now. Please try again.';

    return message || fallbackText;
}

function sortAdmins(list, currentUid) {
    return [...list].sort((a, b) => {
        const aCurrent = a.id === currentUid ? 1 : 0;
        const bCurrent = b.id === currentUid ? 1 : 0;
        if (aCurrent !== bCurrent) return bCurrent - aCurrent;

        const roleRank = {
            [USER_ROLES.MASTER_ADMIN]: 3,
            [USER_ROLES.ADMIN]: 2,
            [USER_ROLES.STAFF]: 1,
            [USER_ROLES.CUSTOMER]: 0,
        };
        const aRole = roleRank[normalizeUserRole(a.role)] || 0;
        const bRole = roleRank[normalizeUserRole(b.role)] || 0;
        if (aRole !== bRole) return bRole - aRole;

        const aActive = a.status === 'active' ? 1 : 0;
        const bActive = b.status === 'active' ? 1 : 0;
        if (aActive !== bActive) return bActive - aActive;

        return (getAdminDisplayLabel(a) || a.email || '').localeCompare(getAdminDisplayLabel(b) || b.email || '');
    });
}

function getAdminDisplayLabel(admin) {
    return admin?.adminName?.trim() || admin?.email?.trim() || 'Admin';
}

async function hydrateAdminEntry(adminEntry) {
    const userSnap = await getDoc(doc(db, 'users', adminEntry.id));
    const userData = userSnap.exists() ? userSnap.data() : {};

    const resolvedRole = normalizeUserRole(
        adminEntry?.role
        || adminEntry?.assignedRole
        || adminEntry?.invitedRole
        || userData?.role
    );

    const resolvedStatus = adminEntry?.status
        || (resolvedRole !== USER_ROLES.CUSTOMER ? 'active' : 'invited');

    return {
        ...adminEntry,
        email: adminEntry?.email || userData?.email || '',
        adminName: adminEntry?.adminName || [userData?.firstName, userData?.lastName].filter(Boolean).join(' ') || '',
        role: resolvedRole,
        status: resolvedStatus,
    };
}

export default function AdminAccountSettings() {
    const { userRole: authUserRole } = useAuth();
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRole, setInviteRole] = useState(USER_ROLES.ADMIN);
    const [inviteLoading, setInviteLoading] = useState(false);
    const [adminMsg, setAdminMsg] = useState(null);
    const [adminList, setAdminList] = useState([]);
    const [adminListLoading, setAdminListLoading] = useState(true);
    const [removeConfirm, setRemoveConfirm] = useState(null);
    const [removeLoadingId, setRemoveLoadingId] = useState(null);

    const [accountInfo, setAccountInfo] = useState({ adminName: '', adminEmail: '' });
    const [accountDraft, setAccountDraft] = useState({ adminName: '', adminEmail: '' });
    const [accountEditing, setAccountEditing] = useState(false);
    const [accountMsg, setAccountMsg] = useState(null);

    const [notifPrefs, setNotifPrefs] = useState({
        newOrderNotifications: true,
        lowStockAlerts: true,
        orderStatusUpdates: true,
    });
    const [notifDraft, setNotifDraft] = useState({ ...notifPrefs });
    const [notifEditing, setNotifEditing] = useState(false);
    const [notifMsg, setNotifMsg] = useState(null);

    const [showPwModal, setShowPwModal] = useState(false);
    const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '' });
    const [pwShow, setPwShow] = useState({ current: false, newPw: false, confirm: false });
    const [pwMsg, setPwMsg] = useState(null);
    const [pwLoading, setPwLoading] = useState(false);

    const currentUser = auth.currentUser;
    const currentUserRole = normalizeUserRole(authUserRole);
    const isStaffAccount = currentUserRole === USER_ROLES.STAFF;
    const canManageAccounts = isMasterAdmin(currentUserRole);

    const callAdminEndpoint = useCallback(async (name, body = {}) => {
        const user = auth.currentUser;
        if (!user) {
            const error = new Error('You must be signed in to continue.');
            error.status = 'unauthenticated';
            throw error;
        }

        if (!functions) {
            throw new Error('Cloud Functions are not configured.');
        }

        const callable = httpsCallable(functions, name);
        const result = await callable(body);
        return result?.data || {};
    }, []);

    const loadAccountSettings = useCallback(async () => {
        try {
            const user = auth.currentUser;
            if (!user || !db) return;

            const snap = await getDoc(doc(db, 'admins', user.uid));
            const data = snap.exists() ? snap.data() : {};
            const info = { adminName: data.adminName || '', adminEmail: user.email || '' };
            setAccountInfo(info);
            setAccountDraft(info);
            if (data.notifications) {
                setNotifPrefs(data.notifications);
                setNotifDraft(data.notifications);
            }
        } catch (err) {
            console.error('Error loading account settings:', err);
        }
    }, [setAccountInfo, setAccountDraft, setNotifPrefs, setNotifDraft]);

    const loadAdminList = useCallback(async () => {
        setAdminListLoading(true);
        try {
            const result = await callAdminEndpoint('listAdmins');
            const list = Array.isArray(result?.admins) ? result.admins : [];
            const hydratedList = await Promise.all(list.map(hydrateAdminEntry));
            setAdminList(sortAdmins(hydratedList, auth.currentUser?.uid || ''));
        } catch (err) {
            console.error('Error loading admin list:', err);
            setAdminMsg({ type: 'error', text: 'Failed to load the current admin list.' });
            setAdminList([]);
        } finally {
            setAdminListLoading(false);
        }
    }, [setAdminListLoading, setAdminList, setAdminMsg, callAdminEndpoint]);

    useEffect(() => {
        loadAccountSettings();
        if (!isStaffAccount) {
            loadAdminList();
        } else {
            setAdminList([]);
            setAdminListLoading(false);
        }
    }, [isStaffAccount, loadAccountSettings, loadAdminList]);

    async function handleSendInvite() {
        if (inviteLoading) return;

        setAdminMsg(null);
        const email = inviteEmail.trim().toLowerCase();

        if (!email) {
            setAdminMsg({ type: 'error', text: 'Please enter an email address.' });
            return;
        }
        if (!validateEmail(email)) {
            setAdminMsg({ type: 'error', text: 'Please enter a valid email address.' });
            return;
        }

        const alreadyAdmin = adminList.some((admin) => admin.email?.toLowerCase() === email);
        if (alreadyAdmin) {
            setAdminMsg({ type: 'error', text: 'This email already has team access.' });
            return;
        }

        setInviteLoading(true);
        try {
            const adminUrl = `${window.location.origin}/admin`;
            const result = await callAdminEndpoint('inviteAdmin', { email, adminUrl, role: inviteRole });
            const status = result?.status || 'invited';
            const roleLabel = getRoleLabel(inviteRole);
            setAdminMsg({
                type: 'success',
                text: status === 'active'
                    ? `${roleLabel} access granted to ${email}. They were notified by email.`
                    : result?.deduplicated
                        ? `${roleLabel} invite was already sent to ${email}.`
                        : `${roleLabel} invite sent to ${email}. They will receive a password setup link.`,
            });
            setInviteEmail('');
            setInviteRole(USER_ROLES.ADMIN);
            await loadAdminList();
        } catch (err) {
            console.error('Error sending invite:', err);
            setAdminMsg({ type: 'error', text: getFriendlyError(err, 'Failed to send invite. Please try again.') });
        } finally {
            setInviteLoading(false);
        }
    }

    async function handleRemoveAdmin(adminId) {
        if (adminId === currentUser?.uid) {
            setAdminMsg({ type: 'error', text: 'You cannot remove your own admin access here.' });
            setRemoveConfirm(null);
            return;
        }

        setAdminMsg(null);
        setRemoveLoadingId(adminId);
        try {
            await callAdminEndpoint('removeAdmin', { uid: adminId });
            setAdminMsg({ type: 'success', text: 'Account access removed successfully.' });
            await loadAdminList();
            setRemoveConfirm(null);
        } catch (err) {
            console.error('Error removing admin:', err);
            setAdminMsg({ type: 'error', text: getFriendlyError(err, 'Failed to remove admin access. Please try again.') });
        } finally {
            setRemoveLoadingId(null);
        }
    }

    function handleAccountEdit() {
        setAccountDraft({ ...accountInfo });
        setAccountEditing(true);
        setAccountMsg(null);
    }

    function handleAccountCancel() {
        setAccountDraft({ ...accountInfo });
        setAccountEditing(false);
        setAccountMsg(null);
    }

    async function handleAccountSave() {
        try {
            const user = auth.currentUser;
            if (!user || !db) throw new Error('No active admin session found.');

            await setDoc(doc(db, 'admins', user.uid), { adminName: accountDraft.adminName }, { merge: true });
            setAccountInfo({ ...accountDraft });
            setAccountEditing(false);
            setAccountMsg({ type: 'success', text: 'Account information updated successfully.' });
        } catch (err) {
            setAccountMsg({ type: 'error', text: getFriendlyError(err, 'Failed to save. Please try again.') });
        }
    }

    function handleNotifCancel() {
        setNotifDraft({ ...notifPrefs });
        setNotifEditing(false);
        setNotifMsg(null);
    }

    async function handleNotifSave() {
        try {
            const user = auth.currentUser;
            if (!user || !db) throw new Error('No active admin session found.');

            await setDoc(doc(db, 'admins', user.uid), { notifications: notifDraft }, { merge: true });
            setNotifPrefs({ ...notifDraft });
            setNotifEditing(false);
            setNotifMsg({ type: 'success', text: 'Notification preferences saved.' });
        } catch (err) {
            setNotifMsg({ type: 'error', text: getFriendlyError(err, 'Failed to save. Please try again.') });
        }
    }

    function handleNotifChange(key) {
        setNotifDraft((prev) => ({ ...prev, [key]: !prev[key] }));
        setNotifEditing(true);
    }

    function handleOpenPwModal() {
        setPwForm({ current: '', newPw: '', confirm: '' });
        setPwShow({ current: false, newPw: false, confirm: false });
        setPwMsg(null);
        setShowPwModal(true);
    }

    function handleClosePwModal() {
        setShowPwModal(false);
        setPwMsg(null);
    }

    async function handleChangePassword() {
        if (!pwForm.current || !pwForm.newPw || !pwForm.confirm) {
            setPwMsg({ type: 'error', text: 'Please fill in all fields.' });
            return;
        }
        if (pwForm.newPw !== pwForm.confirm) {
            setPwMsg({ type: 'error', text: 'New passwords do not match.' });
            return;
        }
        if (pwForm.newPw.length < 6) {
            setPwMsg({ type: 'error', text: 'Password must be at least 6 characters.' });
            return;
        }

        setPwLoading(true);
        try {
            const user = auth.currentUser;
            const credential = EmailAuthProvider.credential(user.email, pwForm.current);
            await reauthenticateWithCredential(user, credential);
            await updatePassword(user, pwForm.newPw);
            setPwMsg({ type: 'success', text: 'Password changed successfully.' });
            setTimeout(() => handleClosePwModal(), 1500);
        } catch (err) {
            if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
                setPwMsg({ type: 'error', text: 'Current password is incorrect.' });
            } else {
                setPwMsg({ type: 'error', text: getFriendlyError(err, 'Failed to change password. Please try again.') });
            }
        } finally {
            setPwLoading(false);
        }
    }

    function togglePwShow(field) {
        setPwShow((prev) => ({ ...prev, [field]: !prev[field] }));
    }

    return (
        <div>
            {!isStaffAccount && (
                <div className="settings-card">
                    <div className="settings-card-header">
                        <h2 className="settings-card-title">Admin Management</h2>
                    </div>

                    <p className="settings-section-label">Add New Team Member</p>
                    <p className="as-section-desc">
                        Invite an Admin or Staff account here. Master Admin access is reserved for your client and
                        should be assigned directly in Firebase.
                    </p>

                    <div className="as-invite-row">
                        <div className="settings-field as-invite-field">
                            <label>Email Address</label>
                            <input
                                type="email"
                                placeholder="Enter email address"
                                value={inviteEmail}
                                onChange={(e) => {
                                    setInviteEmail(e.target.value);
                                    setAdminMsg(null);
                                }}
                                onKeyDown={(e) => e.key === 'Enter' && handleSendInvite()}
                                disabled={inviteLoading || !canManageAccounts}
                            />
                        </div>
                        <div className="settings-field as-invite-role-field">
                            <label>Role</label>
                            <select
                                value={inviteRole}
                                onChange={(e) => {
                                    setInviteRole(e.target.value);
                                    setAdminMsg(null);
                                }}
                                disabled={inviteLoading || !canManageAccounts}
                            >
                                <option value={USER_ROLES.ADMIN}>Admin</option>
                                <option value={USER_ROLES.STAFF}>Staff</option>
                            </select>
                        </div>
                        <button
                            className="as-btn-invite"
                            onClick={handleSendInvite}
                            disabled={inviteLoading || !canManageAccounts}
                        >
                            {inviteLoading ? <SpinnerIcon /> : <SendIcon />}
                            {inviteLoading ? 'Sending...' : 'Send Invite'}
                        </button>
                    </div>

                    {!canManageAccounts && (
                        <div className="settings-msg settings-msg--info" style={{ marginTop: 10 }}>
                            Only the Master Admin can send invites or remove admin/staff accounts.
                        </div>
                    )}

                    {adminMsg && (
                        <div className={`settings-msg settings-msg--${adminMsg.type}`} style={{ marginTop: 10 }}>
                            {adminMsg.text}
                        </div>
                    )}

                    <div className="as-admins-divider" />
                    <p className="settings-section-label">Current Team</p>

                    {adminListLoading ? (
                        <div className="as-admins-loading">
                            <SpinnerIcon /> Loading admins...
                        </div>
                    ) : adminList.length === 0 ? (
                        <div className="as-admins-empty">No admins found.</div>
                    ) : (
                        <div className="as-admins-list">
                            {adminList.map((admin) => {
                                const isCurrentAdmin = admin.id === currentUser?.uid;
                                const isRemoving = removeLoadingId === admin.id;
                                const roleLabel = getRoleLabel(admin.role);
                                const isMasterAccount = normalizeUserRole(admin.role) === USER_ROLES.MASTER_ADMIN;

                                return (
                                    <div key={admin.id} className="as-admin-row">
                                        <div className="as-admin-avatar">
                                            <AdminIcon />
                                        </div>
                                        <div className="as-admin-info">
                                            <div className="as-admin-meta-row">
                                                <span className="as-admin-email">{getAdminDisplayLabel(admin)}</span>
                                                {isCurrentAdmin && <span className="as-admin-you">You</span>}
                                            </div>
                                            {admin.adminName && admin.email && admin.adminName.trim().toLowerCase() !== admin.email.trim().toLowerCase() && (
                                                <span className="as-section-desc" style={{ marginTop: 4 }}>
                                                    {admin.email}
                                                </span>
                                            )}
                                            <div className="as-admin-badges">
                                                <span className={`as-admin-status as-admin-role-badge as-admin-role-badge--${normalizeUserRole(admin.role)}`}>
                                                    {roleLabel}
                                                </span>
                                                {admin.status !== 'active' && (
                                                    <span className="as-admin-status as-admin-status--invited">
                                                        Invite Sent
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="as-admin-actions">
                                            {isCurrentAdmin ? (
                                                <span className="as-admin-self-note">Current account</span>
                                            ) : isMasterAccount ? (
                                                <span className="as-admin-self-note">Protected account</span>
                                            ) : removeConfirm === admin.id ? (
                                                <div className="as-remove-confirm">
                                                    <span>Remove this account?</span>
                                                    <button
                                                        className="as-btn-confirm-yes"
                                                        onClick={() => handleRemoveAdmin(admin.id)}
                                                        disabled={isRemoving}
                                                    >
                                                        {isRemoving ? 'Removing...' : 'Yes, Remove'}
                                                    </button>
                                                    <button
                                                        className="as-btn-confirm-no"
                                                        onClick={() => setRemoveConfirm(null)}
                                                        disabled={isRemoving}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    className="ss-icon-btn ss-icon-btn--danger"
                                                    onClick={() => setRemoveConfirm(admin.id)}
                                                    title="Remove account"
                                                    disabled={!canManageAccounts}
                                                >
                                                    <TrashIcon />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            <div className="settings-card">
                <div className="settings-card-header">
                    <h2 className="settings-card-title">My Account</h2>
                    <div className="settings-card-actions">
                        {!accountEditing ? (
                            <button className="settings-btn-edit" onClick={handleAccountEdit}>
                                <EditIcon /> Edit
                            </button>
                        ) : (
                            <>
                                <button className="settings-btn-cancel" onClick={handleAccountCancel}>Cancel</button>
                                <button className="settings-btn-save" onClick={handleAccountSave}>Save Changes</button>
                            </>
                        )}
                    </div>
                </div>

                {accountMsg && (
                    <div className={`settings-msg settings-msg--${accountMsg.type}`}>
                        {accountMsg.text}
                    </div>
                )}

                <div className="settings-form-grid">
                    <div className="settings-field">
                        <label>Admin Name</label>
                        <input
                            type="text"
                            placeholder="admin name"
                            value={accountEditing ? accountDraft.adminName : accountInfo.adminName}
                            disabled={!accountEditing}
                            onChange={(e) => setAccountDraft({ ...accountDraft, adminName: e.target.value })}
                        />
                    </div>
                    <div className="settings-field">
                        <label>Admin Email Address</label>
                        <input
                            type="email"
                            placeholder="email address"
                            value={accountInfo.adminEmail}
                            disabled
                        />
                    </div>
                </div>

                <button className="settings-btn-change-pw" onClick={handleOpenPwModal}>
                    Change Password
                </button>
            </div>

            {!isStaffAccount && (
                <div className="settings-card">
                    <div className="settings-card-header">
                        <h2 className="settings-card-title">Notification Preferences</h2>
                        <div className="settings-card-actions">
                            {notifEditing && (
                                <>
                                    <button className="settings-btn-cancel" onClick={handleNotifCancel}>Cancel</button>
                                    <button className="settings-btn-save" onClick={handleNotifSave}>Save Changes</button>
                                </>
                            )}
                        </div>
                    </div>

                    {notifMsg && (
                        <div className={`settings-msg settings-msg--${notifMsg.type}`}>
                            {notifMsg.text}
                        </div>
                    )}

                    <p className="settings-section-label">Email Notifications</p>
                    <p className="as-section-desc">
                        These preferences apply to your account only. Each admin can configure their own notification settings independently.
                    </p>
                    <div className="settings-check-group">
                        <label className="settings-check-item">
                            <input
                                type="checkbox"
                                checked={notifDraft.newOrderNotifications}
                                onChange={() => handleNotifChange('newOrderNotifications')}
                            />
                            <div className="settings-check-item-text">
                                <strong>New Order Notifications</strong>
                                <span>Receive email when customers place new orders</span>
                            </div>
                        </label>
                        <label className="settings-check-item">
                            <input
                                type="checkbox"
                                checked={notifDraft.lowStockAlerts}
                                onChange={() => handleNotifChange('lowStockAlerts')}
                            />
                            <div className="settings-check-item-text">
                                <strong>Low Stock Alerts</strong>
                                <span>Receive email when product stock falls below threshold</span>
                            </div>
                        </label>
                        <label className="settings-check-item">
                            <input
                                type="checkbox"
                                checked={notifDraft.orderStatusUpdates}
                                onChange={() => handleNotifChange('orderStatusUpdates')}
                            />
                            <div className="settings-check-item-text">
                                <strong>Order Status Updates</strong>
                                <span>Receive email when order status changes (approved, in production, delivered)</span>
                            </div>
                        </label>
                    </div>
                </div>
            )}

            {showPwModal && (
                <div className="settings-modal-overlay" onClick={handleClosePwModal}>
                    <div className="settings-modal" onClick={(e) => e.stopPropagation()}>
                        <h2>Change Password</h2>
                        <div className="settings-modal-fields">
                            {[
                                { key: 'current', label: 'Current Password:' },
                                { key: 'newPw', label: 'New Password:' },
                                { key: 'confirm', label: 'Confirm New Password:' },
                            ].map(({ key, label }) => (
                                <div className="settings-field" key={key}>
                                    <label>{label}</label>
                                    <div className="settings-password-wrap">
                                        <input
                                            type={pwShow[key] ? 'text' : 'password'}
                                            placeholder="password"
                                            value={pwForm[key]}
                                            onChange={(e) => setPwForm({ ...pwForm, [key]: e.target.value })}
                                        />
                                        <button
                                            type="button"
                                            className="settings-password-toggle"
                                            onClick={() => togglePwShow(key)}
                                        >
                                            {pwShow[key] ? <EyeOffIcon /> : <EyeIcon />}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {pwMsg && (
                            <div className={`settings-msg settings-msg--${pwMsg.type}`}>
                                {pwMsg.text}
                            </div>
                        )}

                        <div className="settings-modal-actions">
                            <button className="settings-btn-cancel" onClick={handleClosePwModal} disabled={pwLoading}>
                                Cancel
                            </button>
                            <button className="settings-btn-save" onClick={handleChangePassword} disabled={pwLoading}>
                                {pwLoading ? 'Applying...' : 'Apply Changes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}